import { CommonModule, ViewportScroller } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, EventEmitter, inject, OnInit, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ResidenceCurrencyService } from '@lx/quote-tool/shared/services';
import { StudentDetails } from '@lx/quote-tool/shared/types';
import { AcquisitionDisclaimerComponent } from '@lx/quote-tool/shared/ui/components';
import { BlockSpacesDirective } from '@lx/quote-tool/shared/ui/directives';
import { CountryRegion, CountryRegionTypeEnum, ResidenceCountry } from '@lx/shared/data-access/destination';
import { GoogleAnalyticsClickEventDirective } from '@lx/shared/google-analytics';
import { getConfiguration } from '@lx/shared/tokens';
import { ButtonComponent } from '@lx/shared/ui/button';
import { KeyboardFocusDirective } from '@lx/shared/ui/keyboard-focus';
import { bubbleAnimation } from '@lx/ui/animations';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { combineLatest, Observable, of, switchMap } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, take } from 'rxjs/operators';
import { FormErrorTrackerDirective } from '../../directives';
import { GoogleAnalyticsEventEnum } from '../../google-analytics';
import { OnlineSchoolService, ResidenceDataService } from '../../services';
import { QuoteToolLiteStore } from '../../store';
import { Step } from '../../types';
import { Flow, Flows, SchoolLocation } from '@lx/quote-tool/lite/shared/types';
import { postalCodeValidator } from '../../validators';
import { customEmailValidator, minPhoneNumberLength } from '@lx/quote-tool/lite/shared/utils';
import { FormFieldComponent, PhoneNumberFieldComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { RegionCountryFieldComponent } from '../region-country-field/region-country-field.component';
import { ResidenceCountrySelectorComponent } from '../residence-country-selector/residence-country-selector.component';
import { parseStringListFromConfigValue } from '@lx/shared/app-config';

@Component({
  standalone: true,
  selector: 'lx-applicant-details',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    FormFieldComponent,
    InputTextModule,
    ResidenceCountrySelectorComponent,
    PhoneNumberFieldComponent,
    RegionCountryFieldComponent,
    CheckboxModule,
    ButtonComponent,
    GoogleAnalyticsClickEventDirective,
    FormErrorTrackerDirective,
    BlockSpacesDirective,
    KeyboardFocusDirective,
    AcquisitionDisclaimerComponent,
    PhoneNumberFieldComponent,
    PhoneNumberFieldComponent,
    PhoneNumberFieldComponent,
  ],
  templateUrl: './applicant-details.component.html',
  styleUrls: ['./applicant-details.component.scss'],
  animations: [bubbleAnimation],
})
export class ApplicantDetailsComponent implements OnInit, AfterViewInit {
  @ViewChild('formContainer') formContainer: ElementRef;
  @Output() submitForm = new EventEmitter<void>();

  form: FormGroup;
  privacyPolicyLink = '';
  whatsappOptIn = false;
  popupDisplayed = false;
  juniorFlow = false;
  firstNameLabel: string | undefined;
  lastNameLabel: string | undefined;
  regions: CountryRegion[] = [];
  postalCodeRegion = false;
  regionPlaceholder = $localize`Select`;
  regionLabel: string | undefined;
  currentStep$: Observable<Step> | undefined;

  emailRegisterGAEvent = GoogleAnalyticsEventEnum.QuoteEmailRegister;

  private readonly configuration = getConfiguration();
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly formBuilder = inject(FormBuilder);
  private readonly residenceDataService = inject(ResidenceDataService);
  private readonly residenceCurrencyService = inject(ResidenceCurrencyService);
  private readonly onlineSchoolService = inject(OnlineSchoolService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly viewportScroller = inject(ViewportScroller);

  ngOnInit(): void {
    this.privacyPolicyLink = <string>this.configuration.QUOTE_TOOL_PRIVACY_POLICY_LINK;
    this.initializeForm();
    this.setPhoneCodeBasedOnCountryOfResidence();
    this.initializeFlow();
    this.initializeWhatsappOptIn();
    this.handleResidenceCountryChanges();
    this.handleRegionCountryChanges();
    this.populateFormWithStudentDetails();
    // Automatically update store when form changes
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.updateStore());
    this.currentStep$ = this.quoteToolLiteStore.currentStep$;
  }

  ngAfterViewInit(): void {
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  togglePopup(): void {
    this.popupDisplayed = !this.popupDisplayed;
  }

  onControlKeydown(event: KeyboardEvent, action: 'toggle' | 'close'): void {
    const key = event.key;
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      if (action === 'toggle') {
        this.togglePopup();
      } else {
        this.closePopup();
      }
    }
  }

  closePopup(): void {
    this.popupDisplayed = false;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.scrollToTop();
      return;
    }
    this.submitForm.emit();
  }

  toggleCheckbox(controlName: string): void {
    const control = this.form.get(controlName);
    if (control) {
      control.setValue(!control.value);
    }
  }

  private setPhoneCodeBasedOnCountryOfResidence() {
    this.quoteToolLiteStore.phoneCode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((phoneCode: string) => this.form.get('phoneNumber')?.setValue(phoneCode, { emitEvent: false }));
  }

  private get fields() {
    return this.form.controls;
  }

  private initializeForm(): void {
    this.form = this.formBuilder.group({
      firstName: ['', [Validators.required, Validators.maxLength(80)]],
      lastName: ['', [Validators.required, Validators.maxLength(40)]],
      countryOfResidence: ['', Validators.required],
      regionCode: [''],
      email: ['', [Validators.required, customEmailValidator()]],
      phoneNumber: ['', [Validators.required, minPhoneNumberLength(5)]],
      optOutMarketing: [false],
    });
  }

  private getFormValues(): StudentDetails {
    const { optOutMarketing, ...formValues } = this.form.value;
    return {
      ...formValues,
      isMinor: this.juniorFlow,
      consentMarketing: !optOutMarketing,
    };
  }

  private initializeFlow(): void {
    this.quoteToolLiteStore.activeFlow$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((activeFlow: Flow) => this.updateFlowState(activeFlow));
  }

  private updateFlowState(activeFlow: Flow): void {
    this.juniorFlow = activeFlow === 'junior';
    this.updateLabels();

    if (this.juniorFlow) {
      this.addGuardianFields();
    }
  }

  private updateLabels(): void {
    this.firstNameLabel = this.juniorFlow ? $localize`Student First Name` : $localize`First Name`;
    this.lastNameLabel = this.juniorFlow ? $localize`Student Last Name` : $localize`Last Name`;
  }

  private addGuardianFields(): void {
    this.form.addControl('guardianFirstName', new FormControl('', [Validators.required]));
    this.form.addControl('guardianLastName', new FormControl('', [Validators.required]));
  }

  private populateFormWithStudentDetails(): void {
    this.quoteToolLiteStore.studentDetails$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((studentDetails: StudentDetails | null) => {
      const countryOfResidence = studentDetails?.countryOfResidence || this.residenceDataService.detectCountryCode();
      const updatedStudentDetails = {
        ...studentDetails,
        countryOfResidence,
        optOutMarketing: studentDetails?.consentMarketing === false,
      };

      this.form.patchValue(updatedStudentDetails);
      this.markFormControlsAsTouched();
    });
  }

  private markFormControlsAsTouched(): void {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);

      // Check if the control has a value before marking it as touched
      if (control?.value !== null && control?.value !== undefined && control?.value !== '') {
        control.markAsTouched();
        control.updateValueAndValidity();

        // Handle phone number fields if applicable
        if (key === 'phoneNumber') {
          this.handlePhoneNumberField(control.value, control);
        }
      }
    });
  }

  private handlePhoneNumberField(phoneNumber: string, control: AbstractControl): void {
    const [, phoneNumberPart] = phoneNumber.split(' '); // Only keep the phone number part

    if (!phoneNumberPart) {
      // Mark phone number as untouched if it's invalid or incomplete
      control.markAsUntouched();
    }
  }

  private updateStore(): void {
    this.quoteToolLiteStore.updateState({ studentDetails: this.getFormValues() });
  }

  // Scroll to top of the form
  private scrollToTop(): void {
    this.formContainer.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start', // Scroll to the start of the form
    });
  }

  private handleResidenceCountryChanges(): void {
    // Extract configuration values once for reuse
    const useMarketCurrency = this.configuration.USE_MARKET_CURRENCY as boolean;
    const useResidenceCurrencyCountries = parseStringListFromConfigValue(this.configuration.USE_RESIDENCE_CURRENCY_COUNTRIES);

    this.fields.countryOfResidence.valueChanges
      .pipe(
        filter(Boolean),
        switchMap((countryCode: string) =>
          combineLatest([
            of(countryCode),
            this.residenceDataService.loadResidenceCountries(),
            this.residenceCurrencyService.getResidenceCurrency(countryCode, useMarketCurrency, useResidenceCurrencyCountries),
            this.quoteToolLiteStore.activeFlow$.pipe(take(1)),
            this.quoteToolLiteStore.selectedSchoolLocation$.pipe(take(1)),
          ]),
        ),
        switchMap(([countryCode, countries, currency, activeFlow, schoolLocation]) => {
          const country = countries.find((c) => c.code === countryCode);
          if (!country) {
            return of(null);
          }

          const baseData = { country, currency };

          if (activeFlow !== Flows.Online) {
            return of({ ...baseData, schoolLocation });
          }

          return this.onlineSchoolService.findOnlineSchoolsForResidenceCountry(countryCode).pipe(
            map(({ onlineSchoolCodes = [] }) => {
              const [firstOnlineCode] = onlineSchoolCodes;
              const updatedSchoolLocation = firstOnlineCode ? { ...schoolLocation, schoolCode: firstOnlineCode } : schoolLocation;

              return { ...baseData, schoolLocation: updatedSchoolLocation };
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        if (!data) {
          return;
        }
        this.applyResidenceChange(data);
      });
  }

  private applyResidenceChange(data: { country: ResidenceCountry; currency: string; schoolLocation?: SchoolLocation }): void {
    const { country, currency, schoolLocation } = data;
    const { code: countryCode, phoneCode, currency: localCurrency, countryRegionType, regions } = country;

    this.quoteToolLiteStore.updateState({
      selectedResidence: { countryCode, phoneCode, currency: localCurrency },
      selectedCurrency: currency,
      selectedSchoolLocation: schoolLocation,
    });

    this.regions = regions ?? [];
    this.postalCodeRegion = countryRegionType === CountryRegionTypeEnum.POSTALCODE;
    this.regionLabel = this.residenceDataService.getCountryRegionLabel(countryRegionType);
    this.resetRegionValidation(country);
  }

  private handleRegionCountryChanges(): void {
    this.fields.regionCode.valueChanges
      .pipe(
        startWith(this.fields.regionCode.value),
        distinctUntilChanged(), // Prevents triggering if value hasn't changed
        switchMap((value) =>
          combineLatest([
            of(value),
            this.quoteToolLiteStore.residenceData$, // Get the latest residence data
          ]),
        ),
        filter(([value, residenceData]) => !!value && !!residenceData), // Ensure both values exist
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([value, residenceData]) => {
        // Check if the value is actually different before updating the state
        if (residenceData.regionCode !== value) {
          this.quoteToolLiteStore.updateState({
            selectedResidence: { ...residenceData, regionCode: value },
          });
        }
      });
  }

  private resetRegionValidation(selectedCountry: ResidenceCountry): void {
    const hasRegion = Boolean(selectedCountry?.countryRegionType);
    const regionField = this.fields.regionCode;

    regionField.setValidators(hasRegion ? [Validators.required, postalCodeValidator(this.postalCodeRegion)] : []);
    regionField.setValue('');
    regionField.updateValueAndValidity();
  }

  private initializeWhatsappOptIn(): void {
    this.whatsappOptIn = <boolean>this.configuration.QUOTE_TOOL_LEAD_WHATSAPP_OPT_IN;
    if (this.whatsappOptIn) {
      this.form.addControl('whatsappOptIn', new FormControl());
    }
  }
}
